/**
 * Get the exponent of a number. Exponent of 0 is -Infinity
 * @param n
 */
export const getExponent = (n: number) => Math.floor(Math.log10(Math.abs(n)));

export function calculateFractionalDigits(values: number[]) {
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const minMaxDiff = maxValue - minValue;

    if (new Set(values).size < 2) {
        return 1;
    }

    const minMaxDifferenceExponent = getExponent(minMaxDiff);
    const maxExponent = getExponent(maxValue);
    const interval = values[1] - values[0];
    let fractionDigits = maxExponent - minMaxDifferenceExponent;

    if (minMaxDifferenceExponent % 3 === 0 && maxExponent % 3 === 0 && minMaxDifferenceExponent === maxExponent) {
        fractionDigits = 1;
    }
    if (fractionDigits === 1 && interval % minMaxDifferenceExponent === 0) {
        fractionDigits = 2;
    }

    const labelsFD = fractionDigits > 0 ? (fractionDigits < 100 ? fractionDigits : 99) : 0;

    return labelsFD;
}

export const formatYAxisLabels = (n: number, fractionDigits: number = 0) => {
    if (isNaN(n)) {
        console.log('Error: number is NaN');
        return '';
    }

    const exponent = getExponent(n);
    const format = (n: number) => {
        return parseFloat((n / Math.pow(10, exponent - (exponent % 3))).toFixed(fractionDigits));
    };

    const sliceRules = [
        {
            threshold: 3,
            exponentSign: '',
            format: (n: number) => n,
        },
        {
            threshold: 6,
            exponentSign: 'K',
            format,
        },
        {
            threshold: 9,
            exponentSign: 'M',
            format,
        },
        {
            threshold: Infinity,
            exponentSign: 'B',
            format,
        },
    ];

    const rule = sliceRules.find((r) => r.threshold > exponent);
    if (!rule) {
        return n.toString();
    }
    if (`${rule.format(n)}${rule.exponentSign}`.length === n.toString().length + 1) {
        return n.toString();
    }

    return `${rule.format(n)}${rule.exponentSign}`;
};

/**
 * Chart.js passes bugged values as chart ticks when dealing with decimals.
 * This function cleans them up.
 * @param n
 */
export const cleanValue = (value: number) => {
    if (Number.isInteger(value)) {
        return value;
    }
    const decimalSlice = value.toString().split('.')[1];
    const zeroesInValue = decimalSlice.split('').filter((char) => char === '0').length;

    if (zeroesInValue > 10) {
        return Math.round(value * 100) / 100;
    }
    return value;
};
